import { z } from 'zod'

/**
 * What the model is allowed to add.
 *
 * Everything numeric on an action is the detector's; this is prose, an
 * ordering hint and a veto. The model never produces a figure, because a figure
 * it produced could not be reconciled against the evidence the row already
 * carries — and the row has to survive the model being unavailable, so the
 * whole of this is optional decoration over an action that already stands up.
 *
 * `skip` is the important field. Detectors are written for recall — a band, a
 * threshold, a join — and the cost of a loose threshold is a list nobody
 * trusts. The model is the precision half: it sees the page, the results page
 * and the numbers together, and it is asked to throw the row away when those
 * three do not agree. That is a judgement no `HAVING` clause can make.
 */
export const actionEnrichmentSchema = z.object({
  /** Replaces the detector's title. An imperative naming this page or keyword,
   * not a restatement of the category. */
  headline: z.string().min(4).max(120),
  /** One short paragraph: what is happening and why it is worth doing. */
  summary: z.string().min(10).max(600),
  /** What to actually do, in order. Written for somebody who does not know what
   * a canonical is — the product is for people who do not employ an SEO. */
  steps: z.array(z.string().min(4).max(400)).min(1).max(5),
  /**
   * The concrete artefact where the job has one: a title to paste, an anchor,
   * a redirect target. Generic rather than per-type because the alternative is
   * a schema per action type and a migration each time one is added.
   */
  suggestion: z.string().max(400).nullable(),
  /** 0-1. Feeds the ordering, so a model that is unsure sinks its own row
   * rather than needing a separate reviewer to catch it. */
  confidence: z.number().min(0).max(1),
  /** The veto. True means the evidence does not support showing this at all. */
  skip: z.boolean(),
  /** Why it was thrown away. Read in /admin, never by the customer — a rejected
   * action they never saw needs no explanation, and this is how a threshold
   * that is too loose becomes visible. */
  skipReason: z.string().max(400).nullable(),
})

export type ActionEnrichment = z.infer<typeof actionEnrichmentSchema>

/**
 * The gateway takes a JSON Schema, and it has to be the same shape the parse on
 * the way back uses — deriving it from the zod schema is what stops the two
 * drifting.
 *
 * Strict mode is fussier than the draft it names: it rejects `$schema`, demands
 * `additionalProperties: false`, and requires *every* property to be listed as
 * required. That last one is why the two nullable fields are `.nullable()`
 * rather than `.nullish()` — an optional field is one strict mode refuses,
 * where a required field that may be null says the same thing and validates.
 */
export function actionEnrichmentJsonSchema(): Record<string, unknown> {
  const { $schema, ...schema } = z.toJSONSchema(actionEnrichmentSchema, {
    target: 'draft-7',
  }) as Record<string, unknown> & { $schema?: string }

  return { ...schema, additionalProperties: false }
}

/** What goes in `ai_prompt_version.params`, and therefore on the run. Strict
 * because a model that answers with prose around the object costs the same as
 * one that answers with the object. */
export function actionResponseFormat(): Record<string, unknown> {
  return {
    response_format: {
      type: 'json_schema',
      json_schema: {
        name: 'seo_action',
        strict: true,
        schema: actionEnrichmentJsonSchema(),
      },
    },
  }
}
